/**
 * The reader's own dashboard on the engine (`/v1/dashboard*`, mitra-ai
 * 2026-10-18): the widgets they keep, each a formula over their book,
 * worked out by the engine every time it is read.
 *
 * EVERY VALUE IS THE ENGINE'S STRING. A widget's figure is worked out in
 * `Decimal` on the engine and arrives as an exact decimal string; nothing
 * here turns it into a number for display. The parsers below are defensive
 * for the same reason `actions.ts`'s are: a body that is not what the
 * contract says is dropped, never drawn half-understood.
 *
 * NEVER REJECTS. Each call answers with a result the screen can draw --
 * a body, a conflict, the engine's refusal, or `failed` -- so a dashboard
 * that cannot be read is a state on the page and never an unhandled error.
 * Through `engineFetch` like every other engine call, so the bearer is the
 * engine's own and a 401 never refreshes the MoneyMitra session.
 */
import { engineFetch } from '@/features/mitra/api/askClient'

export type WidgetKind = 'number' | 'column' | 'bars' | 'sector_bars'
export type WidgetUnit = 'NPR' | 'percent' | 'ratio' | 'shares' | 'count'

/** The engine's cap (`dashboard/model.py` MAX_WIDGETS); it refuses past it. */
export const MAX_WIDGETS = 24

export const KINDS: readonly WidgetKind[] = ['number', 'column', 'bars', 'sector_bars']
export const UNITS: readonly WidgetUnit[] = ['NPR', 'percent', 'ratio', 'shares', 'count']

/**
 * TIME AND BROKERS (mitra-ai "Analytics Studio: time and brokers", contract
 * `studio-history-contract.md`). No new widget kinds: any widget may carry a
 * window it is worked out over step by step (`time`), and a widget may be
 * over brokers rather than companies (`scope.kind = "brokers"`). A widget
 * with neither is the widget it always was, and none of the fields below
 * appear on it -- so every parser here treats them as absent, never as null
 * meaning "today".
 */
export type TimeWindow = '1W' | '1M' | '3M' | '6M' | '1Y' | 'YTD' | 'custom'
export type TimeEvery = 'day' | 'week' | 'month'
export type TimeShow = 'line' | 'bars' | 'table'
export type Rank = 'desc' | 'asc' | 'abs'

export const WINDOWS: readonly TimeWindow[] = ['1W', '1M', '3M', '6M', '1Y', 'YTD', 'custom']
export const EVERIES: readonly TimeEvery[] = ['day', 'week', 'month']
export const SHOWS: readonly TimeShow[] = ['line', 'bars', 'table']
export const RANKS: readonly Rank[] = ['desc', 'asc', 'abs']

/** A widget's time dimension as stored: `every: null` is one figure over the
 *  whole window (how a broker table is asked for); `show` is a drawing hint. */
export interface TimeSpec {
  window: TimeWindow
  /** ISO dates, only with `window: 'custom'`. */
  from: string | null
  to: string | null
  every: TimeEvery | null
  show: TimeShow | null
}

/** One step of a series: `date` its last session, `from` its first. A step
 *  with no figure is null with the names that were missing, never filled in. */
export interface SeriesPoint {
  date: string
  from: string
  value: string | null
  missing: string[]
}

/** One line of a series: a company, a broker, a sector, or the widget itself. */
export interface Series {
  label: string
  points: SeriesPoint[]
}

/** What a windowed widget's figures actually cover. */
export interface Period {
  from: string
  to: string
  sessions: number
}

/** Where a windowed widget's figures were read from, for the "covers" line. */
export interface Sources {
  prices: 'adjusted' | 'as_traded' | null
  tradesLedger: boolean
  floorsheet: (Period & { capped: boolean; lastCovered: string | null }) | null
  brokerTotals: Period | null
}

/** A value and why there is none, for one widget or one point of it. */
export interface Worked {
  value: string | null
  missing: string[]
  left_out: number
  left_out_for: string[]
}

export type Point = Worked & { label: string }

/** A repair the engine checked reads: the formula after it, and a kind or
 *  unit where the repair is to change how the widget draws instead. */
export interface Fix {
  label: string
  formula?: string | undefined
  kind?: WidgetKind | undefined
  unit?: WidgetUnit | undefined
}

/** Something a finance reader would want fixed, with its correction and,
 *  over their own book, both values. */
export interface CheckWarning {
  code: string
  message: string
  position: number
  end: number
  fix?: Fix | undefined
  compare?: { yours: string | null; better: string | null } | undefined
}

/** The engine's check-up of one formula (`dashboard/doctor.py`). */
export interface Check {
  ok: boolean
  error: { message: string; position: number; end: number; code: string } | null
  fixes: Fix[]
  warnings: CheckWarning[]
  unit: WidgetUnit | null
  unit_name: string | null
  explanation: string | null
  picture: Picture | null
  /** Words that are nothing Mitra holds, offered as the reader's own number. */
  new_numbers: { word: string; name: string }[]
}

/** What a widget is worked out over (mitra-ai round 3): the reader's own
 *  holdings, companies they named, one of their watchlists, or the market. */
export type Scope =
  | { kind: 'book' }
  | { kind: 'symbols'; symbols: string[] }
  | { kind: 'watchlist'; id: number }
  | { kind: 'market' }
  /** Rows are brokers trading these symbols (1 to 3); `[]` is the whole market. */
  | { kind: 'brokers'; symbols: string[] }

/**
 * A formula as a picture (`check.picture`, `dashboard/doctor.py display`):
 * chips for names, a stacked fraction for a division, a "%" badge for
 * `x / y * 100`, an aggregate with its filter as a tag. Drawn instead of the
 * formula's code or a sentence about it.
 */
export type Picture =
  /** `mine`: one of the reader's own numbers, drawn as theirs. */
  | { t: 'name'; name: string; label: string; dim: string; mine?: boolean | undefined }
  | { t: 'num'; value: string }
  | { t: 'text'; value: string }
  | { t: 'neg'; of: Picture }
  | { t: 'frac'; num: Picture; den: Picture }
  | { t: 'pct'; num: Picture; den: Picture }
  | { t: 'op'; op: string; left: Picture; right: Picture }
  | {
      t: 'agg'
      fn: string
      label: string
      of?: Picture | undefined
      by?: Picture | undefined
      where?: string | undefined
    }
  | { t: 'fn'; fn: string; label: string; args: Picture[] }

/** How many rows a value stands on, for the coverage meter. */
export interface Coverage {
  counted: number
  total: number
}

/** One total of a single-figure widget, holding by holding. */
export interface BreakdownPart {
  part: string
  name: string
  rows: { label: string; value: string | null; counted: boolean; missing: string[] }[]
}

/** One widget as the engine drew it: its definition and its values now. */
export interface Drawn extends Partial<Worked> {
  id: string | null
  title: string
  formula: string
  kind: WidgetKind
  unit: WidgetUnit
  decimals: number
  error: { message: string; position: number | null } | null
  /** Who wrote it: Mitra's widgets say so until the reader changes them. */
  origin: 'you' | 'mitra'
  check: Check | null
  breakdown: BreakdownPart[]
  points?: Point[] | undefined
  section: string | null
  scope: Scope
  /** Which of the body's `scopes` this widget's rows are. */
  scopeKey: string | null
  coverage: Coverage | null
  /* -- Over time and over brokers: absent on a widget that uses neither. -- */
  time?: TimeSpec | undefined
  rank?: Rank | undefined
  limit?: number | undefined
  /** Column and bars only: which rows are shown, a condition in the formula language. */
  where?: string | undefined
  /** The server adjusted what was asked (a P/E averaged, say): what it was written as. */
  fixedFrom?: string | undefined
  /** What the figures cover; null where nothing could be read. */
  period?: Period | null | undefined
  /** The server's sentence of what the figures show; null where `reason` is set. */
  note?: string | null | undefined
  /** Why the widget could not be filled; its definition is still fine. */
  reason?: string | null | undefined
  sources?: Sources | null | undefined
  /** Plain sentences about partial coverage, never errors. */
  warnings?: string[] | undefined
  /** With `time.every` set: one line per row (or one, for a single figure). */
  series?: Series[] | undefined
}

/** A widget as the reader edits it and the engine saves it. */
export interface WidgetDraft {
  id?: string | undefined
  title: string
  formula: string
  kind: WidgetKind
  unit: WidgetUnit
  decimals: number
  section?: string | null | undefined
  scope?: Scope | undefined
  /** `null` turns a series back into today's figure; absent keeps what is stored. */
  time?: TimeSpec | null | undefined
  rank?: Rank | null | undefined
  limit?: number | null | undefined
  /** Which rows a column or bars shows; `null` shows every row again. */
  where?: string | null | undefined
}

/** What one of the reader's own numbers is in (`dashboard/model.py` MyNumber). */
export type NumberUnit = 'NPR' | 'price' | 'percent' | 'ratio' | 'count' | 'shares'
export const NUMBER_UNITS: readonly NumberUnit[] = [
  'NPR',
  'price',
  'percent',
  'ratio',
  'count',
  'shares',
]

/**
 * A number the reader sets themselves and uses in any formula by `name`: one
 * value ("my_target_yield" 6), or a value per company. Mitra holds no such
 * data; the reader does. Values are the reader's decimal strings.
 */
export interface MyNumber {
  name: string
  label: string
  unit: NumberUnit
  value: string | null
  values: Record<string, string>
}

/** One dashboard as the switcher lists it. */
export interface BoardSummary {
  id: string
  name: string
  purpose: string
  origin: 'you' | 'mitra'
  widgets: number
  /** When the dashboard was made (ISO), where the engine says; null otherwise. */
  createdAt: string | null
  /** The titles of the measures it holds, in order, where the engine sends them. */
  measures: string[]
  /** The template it was made from ("stock_watch:UMHL"), where the engine says. */
  template: string | null
}

/** One kept earlier state of a dashboard, newest first. */
export interface Version {
  index: number
  at: string
  by: 'you' | 'mitra'
  label: string
  name: string
  widgets: number
}

/** A set of rows widgets are worked out over, named for its table. */
export interface ScopeRows {
  key: string
  kind: Scope['kind']
  label: string
  rows: string[]
}

/** A whole dashboard to start from. */
export interface Template {
  key: string
  name: string
  purpose: string
  sections: { title: string; measures: string[] }[]
  /** The template is about one company: `POST /v1/dashboards` needs `symbol`. */
  needsSymbol: boolean
}

/** A measure the no-formula builder offers, and what it can be shown for. */
export interface Measure {
  name: string
  label: string
  unit: string
  group: string
  over: ('book' | 'each' | 'sector')[]
}

export interface DashboardBody {
  etag: string | null
  version: number
  widgets: Drawn[]
  holdings: string[]
  as_of: string | null
  data_state: string | null
  unavailable: string[]
  /** The dashboard drawn: null where the reader has none yet. */
  dashboard: (BoardSummary & { sections: (string | null)[] }) | null
  boards: BoardSummary[]
  history: Version[]
  scopes: ScopeRows[]
  /** The reader's own numbers, shared by all their dashboards. */
  numbers: MyNumber[]
  /**
   * A create from a template the reader already has, answered with that
   * dashboard instead of a copy (contract §6). An engine that refuses it as
   * `name_taken` instead says so through `SaveResult`; either way the screen
   * opens the one the reader has.
   */
  existing: boolean
}

export interface PreviewBody {
  widgets: Drawn[]
  holdings: string[]
  scopes: ScopeRows[]
}

export interface Variable {
  name: string
  kind: 'number' | 'text'
  unit: string
  group: string
  meaning: string
}

export type Preset = WidgetDraft & {
  key: string
  group: string
  about: string
  /** Its `scope.symbols` holds `"{symbol}"`, for the company the reader picks. */
  needsSymbol: boolean
}

/** A name that has a record over time, and what kind of figure it is. */
export interface HistoryVariable {
  name: string
  unit: string
  /** `level` is carried forward to a step's end; `flow` is summed over it. */
  nature: 'level' | 'flow' | null
  source: string | null
  meaning: string
}

/** A figure each broker row has. */
export interface BrokerVariable {
  name: string
  unit: string
  meaning: string
}

/** The time dimension's choices, as the engine offers them. */
export interface TimeVocabulary {
  windows: { key: TimeWindow; label: string; days: number | null }[]
  every: { key: TimeEvery; label: string }[]
  show: TimeShow[]
  defaultEvery: Partial<Record<TimeWindow, TimeEvery>>
  limits: {
    symbols: number | null
    floorsheetSymbols: number | null
    floorsheetSessions: number | null
    steps: number | null
    customDays: number | null
  }
}

export interface Vocabulary {
  variables: Variable[]
  functions: { name: string; meaning: string }[]
  presets: Preset[]
  /** One of the reader's own holdings' value for each name. */
  samples: Record<string, { symbol: string; value: string }>
  /** The sectors and symbols the reader holds, for a filter's picker. */
  held: { sector: string[]; symbol: string[] }
  /** Everyday and Nepali words for a name ("kitta" for quantity). */
  aliases: Record<string, string>
  measures: Measure[]
  templates: Template[]
  watchlists: { id: number; name: string }[]
  numbers: MyNumber[]
  /** The everyday phrases the engine reads as formulas ("dividend yield"). */
  phrases: string[]
  /** Over time: null from an engine that has no time dimension. */
  time: TimeVocabulary | null
  historyVariables: HistoryVariable[]
  brokerVariables: BrokerVariable[]
  /** The scope kinds this engine takes; the four of round 3 where it says none. */
  scopes: Scope['kind'][]
  rank: Rank[]
}

const isObject = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value)

const strings = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((v) => typeof v === 'string')

/** A decimal as the engine writes one. Nothing else is drawn as a value. */
const DECIMAL = /^-?\d+(\.\d+)?$/

const decimalOrNull = (value: unknown): value is string | null =>
  value === null || (typeof value === 'string' && DECIMAL.test(value))

const isKind = (value: unknown): value is WidgetKind => KINDS.includes(value as WidgetKind)
const isUnit = (value: unknown): value is WidgetUnit => UNITS.includes(value as WidgetUnit)

const integer = (value: unknown, fallback = 0): number =>
  typeof value === 'number' && Number.isSafeInteger(value) ? value : fallback

function workedOf(value: Record<string, unknown>): Worked | null {
  if (
    !decimalOrNull(value.value) ||
    !strings(value.missing) ||
    !Number.isSafeInteger(value.left_out) ||
    !strings(value.left_out_for)
  ) {
    return null
  }
  return {
    value: value.value,
    missing: value.missing,
    left_out: integer(value.left_out),
    left_out_for: value.left_out_for,
  }
}

function fixOf(value: unknown): Fix | null {
  if (!isObject(value) || typeof value.label !== 'string') return null
  if (value.formula !== undefined && typeof value.formula !== 'string') return null
  if (value.kind !== undefined && !isKind(value.kind)) return null
  if (value.unit !== undefined && !isUnit(value.unit)) return null
  return {
    label: value.label,
    ...(typeof value.formula === 'string' ? { formula: value.formula } : {}),
    ...(isKind(value.kind) ? { kind: value.kind } : {}),
    ...(isUnit(value.unit) ? { unit: value.unit } : {}),
  }
}

/** A picture node, or null where any part of it is not one the contract draws. */
export function pictureOf(value: unknown, depth = 0): Picture | null {
  if (!isObject(value) || depth > 24) return null
  const sub = (v: unknown) => pictureOf(v, depth + 1)
  switch (value.t) {
    case 'name':
      return typeof value.name === 'string' && typeof value.label === 'string'
        ? {
            t: 'name',
            name: value.name,
            label: value.label,
            dim: typeof value.dim === 'string' ? value.dim : 'ratio',
            ...(value.mine === true ? { mine: true } : {}),
          }
        : null
    case 'num':
    case 'text':
      return typeof value.value === 'string' ? { t: value.t, value: value.value } : null
    case 'neg': {
      const of = sub(value.of)
      return of ? { t: 'neg', of } : null
    }
    case 'frac':
    case 'pct': {
      const num = sub(value.num)
      const den = sub(value.den)
      return num && den ? { t: value.t, num, den } : null
    }
    case 'op': {
      const left = sub(value.left)
      const right = sub(value.right)
      return left && right && typeof value.op === 'string'
        ? { t: 'op', op: value.op, left, right }
        : null
    }
    case 'agg': {
      if (typeof value.fn !== 'string' || typeof value.label !== 'string') return null
      const of = value.of === undefined ? undefined : sub(value.of)
      const by = value.by === undefined ? undefined : sub(value.by)
      if (of === null || by === null) return null
      return {
        t: 'agg',
        fn: value.fn,
        label: value.label,
        ...(of ? { of } : {}),
        ...(by ? { by } : {}),
        ...(typeof value.where === 'string' ? { where: value.where } : {}),
      }
    }
    case 'fn': {
      if (typeof value.fn !== 'string' || typeof value.label !== 'string') return null
      if (!Array.isArray(value.args)) return null
      const args = value.args.map(sub)
      if (args.some((a) => a === null)) return null
      return { t: 'fn', fn: value.fn, label: value.label, args: args as Picture[] }
    }
    default:
      return null
  }
}

/** A scope as the engine writes one; the reader's holdings where it names none. */
export function scopeOf(value: unknown): Scope {
  if (!isObject(value)) return { kind: 'book' }
  if (value.kind === 'symbols' && strings(value.symbols) && value.symbols.length > 0) {
    return { kind: 'symbols', symbols: value.symbols }
  }
  if (value.kind === 'watchlist' && Number.isSafeInteger(value.id)) {
    return { kind: 'watchlist', id: integer(value.id) }
  }
  if (value.kind === 'market') return { kind: 'market' }
  // An empty list is meaningful here (the whole market's brokers), unlike symbols'.
  if (value.kind === 'brokers' && strings(value.symbols)) {
    return { kind: 'brokers', symbols: value.symbols }
  }
  return { kind: 'book' }
}

/** An ISO date as the engine writes one. */
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/

const isoOrNull = (value: unknown): string | null =>
  typeof value === 'string' && ISO_DATE.test(value) ? value : null

const oneOf = <T extends string>(list: readonly T[], value: unknown): T | null =>
  list.includes(value as T) ? (value as T) : null

/**
 * What an auto-fixed widget was written as (`fixed_from`): the formula as
 * asked, or `{formula, why}` with the reason. Said quietly beside it as
 * "Adjusted: …", never as an error.
 */
export function fixedFromOf(value: unknown): string | null {
  if (typeof value === 'string') return value.trim() || null
  if (!isObject(value)) return null
  const formula = typeof value.formula === 'string' ? value.formula.trim() : ''
  const why = typeof value.why === 'string' ? value.why.trim() : ''
  if (!formula && !why) return null
  return formula && why ? `${formula} (${why})` : formula || why
}

/**
 * A widget definition as a chat proposal carries one (the card's `widgets`,
 * a refusal's `suggestion`), or null where it is not one this screen can
 * edit. The same fields a save sends; nothing worked out.
 */
export function definitionOf(value: unknown): (WidgetDraft & { fixedFrom?: string }) | null {
  if (!isObject(value)) return null
  if (
    typeof value.title !== 'string' ||
    typeof value.formula !== 'string' ||
    !isKind(value.kind) ||
    !isUnit(value.unit)
  ) {
    return null
  }
  const time = timeOf(value.time)
  const rank = oneOf(RANKS, value.rank)
  const fixed = fixedFromOf(value.fixed_from)
  return {
    // An edit names the widget it changes; an add has none (or a placeholder).
    ...(typeof value.id === 'string' && /^w_[0-9a-z]{10}$/.test(value.id) ? { id: value.id } : {}),
    title: value.title,
    formula: value.formula,
    kind: value.kind,
    unit: value.unit,
    decimals: integer(value.decimals, 2),
    ...(typeof value.section === 'string' && value.section ? { section: value.section } : {}),
    ...(value.scope !== undefined ? { scope: scopeOf(value.scope) } : {}),
    ...(time ? { time } : {}),
    ...(rank ? { rank } : {}),
    ...(typeof value.limit === 'number' && Number.isSafeInteger(value.limit)
      ? { limit: value.limit }
      : {}),
    ...(typeof value.where === 'string' && value.where.trim() ? { where: value.where } : {}),
    ...(fixed ? { fixedFrom: fixed } : {}),
  }
}

/**
 * A time dimension as the engine writes one, or null where there is none or
 * it is not one the contract describes. A window is required; the rest is
 * tolerant (an unknown `show` is no hint, a custom window without both dates
 * is not a window).
 */
export function timeOf(value: unknown): TimeSpec | null {
  if (!isObject(value)) return null
  const window = oneOf(WINDOWS, value.window)
  if (!window) return null
  const from = isoOrNull(value.from)
  const to = isoOrNull(value.to)
  if (window === 'custom' && (!from || !to)) return null
  return {
    window,
    from: window === 'custom' ? from : null,
    to: window === 'custom' ? to : null,
    every: oneOf(EVERIES, value.every),
    show: oneOf(SHOWS, value.show),
  }
}

function periodOf(value: unknown): Period | null {
  if (!isObject(value)) return null
  const from = isoOrNull(value.from)
  const to = isoOrNull(value.to)
  if (!from || !to) return null
  return { from, to, sessions: integer(value.sessions) }
}

function sourcesOf(value: unknown): Sources | null {
  if (!isObject(value)) return null
  const floor = periodOf(value.floorsheet)
  return {
    prices: oneOf(['adjusted', 'as_traded'] as const, value.prices),
    tradesLedger: value.trades_ledger === true,
    floorsheet:
      floor && isObject(value.floorsheet)
        ? {
            ...floor,
            capped: value.floorsheet.capped === true,
            lastCovered: isoOrNull(value.floorsheet.last_covered),
          }
        : null,
    brokerTotals: periodOf(value.broker_totals),
  }
}

/**
 * A series as the engine draws one, or null where any line or step is not
 * what the contract says: a series half-understood would draw a wrong line.
 */
function seriesOf(value: unknown): Series[] | null {
  if (!Array.isArray(value)) return null
  const out: Series[] = []
  for (const line of value) {
    if (!isObject(line) || typeof line.label !== 'string' || !Array.isArray(line.points)) {
      return null
    }
    const points: SeriesPoint[] = []
    for (const raw of line.points) {
      if (!isObject(raw)) return null
      const date = isoOrNull(raw.date)
      if (!date || !decimalOrNull(raw.value)) return null
      points.push({
        date,
        from: isoOrNull(raw.from) ?? date,
        value: raw.value,
        missing: strings(raw.missing) ? raw.missing : [],
      })
    }
    out.push({ label: line.label, points })
  }
  return out
}

/** The windowed fields of a drawn widget, only those the engine sent. */
function windowedOf(value: Record<string, unknown>): Partial<Drawn> {
  const time = timeOf(value.time)
  const rank = oneOf(RANKS, value.rank)
  const limit =
    typeof value.limit === 'number' && Number.isSafeInteger(value.limit) && value.limit > 0
      ? value.limit
      : null
  const out: Partial<Drawn> = {}
  if (time) out.time = time
  if (rank) out.rank = rank
  if (limit !== null) out.limit = limit
  if (typeof value.where === 'string' && value.where.trim()) out.where = value.where
  const fixed = fixedFromOf(value.fixed_from)
  if (fixed) out.fixedFrom = fixed
  if ('period' in value) out.period = periodOf(value.period)
  if ('note' in value) out.note = typeof value.note === 'string' && value.note ? value.note : null
  if ('reason' in value) {
    out.reason = typeof value.reason === 'string' && value.reason ? value.reason : null
  }
  if ('sources' in value) out.sources = sourcesOf(value.sources)
  if (Array.isArray(value.warnings)) {
    out.warnings = value.warnings.filter((w): w is string => typeof w === 'string' && w !== '')
  }
  return out
}

function coverageOf(value: unknown): Coverage | null {
  if (!isObject(value)) return null
  if (!Number.isSafeInteger(value.counted) || !Number.isSafeInteger(value.total)) return null
  return { counted: integer(value.counted), total: integer(value.total) }
}

/** A check-up as the engine writes one, or nothing that could mislead. */
export function checkOf(value: unknown): Check | null {
  if (!isObject(value) || !Array.isArray(value.fixes) || !Array.isArray(value.warnings)) {
    return null
  }
  const error =
    isObject(value.error) && typeof value.error.message === 'string'
      ? {
          message: value.error.message,
          position: integer(value.error.position),
          end: integer(value.error.end),
          code: typeof value.error.code === 'string' ? value.error.code : 'invalid',
        }
      : null
  const warnings: CheckWarning[] = []
  for (const raw of value.warnings) {
    if (!isObject(raw) || typeof raw.code !== 'string' || typeof raw.message !== 'string') continue
    const fix = raw.fix === undefined ? null : fixOf(raw.fix)
    if (raw.fix !== undefined && fix === null) continue
    const compare =
      isObject(raw.compare) && decimalOrNull(raw.compare.yours) && decimalOrNull(raw.compare.better)
        ? { yours: raw.compare.yours, better: raw.compare.better }
        : undefined
    warnings.push({
      code: raw.code,
      message: raw.message,
      position: integer(raw.position),
      end: integer(raw.end),
      ...(fix ? { fix } : {}),
      ...(compare ? { compare } : {}),
    })
  }
  return {
    ok: value.ok === true,
    error,
    fixes: value.fixes.map(fixOf).filter((f): f is Fix => f !== null),
    warnings,
    unit: isUnit(value.unit) ? value.unit : null,
    unit_name: typeof value.unit_name === 'string' ? value.unit_name : null,
    explanation: typeof value.explanation === 'string' ? value.explanation : null,
    picture: pictureOf(value.picture),
    new_numbers: listOf(value.new_numbers, (n) =>
      isObject(n) && typeof n.word === 'string' && typeof n.name === 'string'
        ? { word: n.word, name: n.name }
        : null,
    ),
  }
}

function breakdownOf(value: unknown): BreakdownPart[] {
  if (!Array.isArray(value)) return []
  const parts: BreakdownPart[] = []
  for (const part of value) {
    if (!isObject(part) || typeof part.part !== 'string' || typeof part.name !== 'string') continue
    if (!Array.isArray(part.rows)) continue
    const rows: BreakdownPart['rows'] = []
    for (const row of part.rows) {
      if (
        isObject(row) &&
        typeof row.label === 'string' &&
        typeof row.counted === 'boolean' &&
        decimalOrNull(row.value) &&
        strings(row.missing)
      ) {
        rows.push({
          label: row.label,
          value: row.value,
          counted: row.counted,
          missing: row.missing,
        })
      }
    }
    parts.push({ part: part.part, name: part.name, rows })
  }
  return parts
}

/** One drawn widget, or null where it is not one the contract describes. */
export function drawnOf(value: unknown): Drawn | null {
  if (!isObject(value)) return null
  if (
    typeof value.title !== 'string' ||
    typeof value.formula !== 'string' ||
    !isKind(value.kind) ||
    !isUnit(value.unit) ||
    !Number.isSafeInteger(value.decimals)
  ) {
    return null
  }
  const base = {
    id: typeof value.id === 'string' ? value.id : null,
    title: value.title,
    formula: value.formula,
    kind: value.kind,
    unit: value.unit,
    decimals: integer(value.decimals, 2),
    origin: value.origin === 'mitra' ? ('mitra' as const) : ('you' as const),
    check: checkOf(value.check),
    breakdown: breakdownOf(value.breakdown),
    section: typeof value.section === 'string' && value.section ? value.section : null,
    scope: scopeOf(value.scope),
    scopeKey: typeof value.scope_key === 'string' ? value.scope_key : null,
    coverage: coverageOf(value.coverage),
    ...windowedOf(value),
  }
  if (value.error !== null && value.error !== undefined) {
    if (!isObject(value.error) || typeof value.error.message !== 'string') return null
    const position = value.error.position
    return {
      ...base,
      error: {
        message: value.error.message,
        position: typeof position === 'number' && Number.isSafeInteger(position) ? position : null,
      },
    }
  }
  // A widget the data could not fill: its reason, and nothing drawn. The
  // definition is fine (`error` null), so it is a state, not a failure.
  if (base.reason) {
    return {
      ...base,
      error: null,
      value: null,
      missing: [],
      left_out: 0,
      left_out_for: [],
      ...(value.kind === 'number' ? {} : { points: [] }),
      ...(base.time?.every ? { series: [] } : {}),
    }
  }
  // A series (`time.every` set): its lines, and for one figure the last
  // step's value too, so a card that knows no series still shows a figure.
  if (Array.isArray(value.series)) {
    const series = seriesOf(value.series)
    if (!series || !decimalOrNull(value.value ?? null)) return null
    return {
      ...base,
      error: null,
      series,
      value: (value.value as string | null | undefined) ?? null,
      missing: strings(value.missing) ? value.missing : [],
      left_out: integer(value.left_out),
      left_out_for: strings(value.left_out_for) ? value.left_out_for : [],
    }
  }
  if (value.kind === 'number') {
    const worked = workedOf(value)
    return worked ? { ...base, error: null, ...worked } : null
  }
  if (!Array.isArray(value.points)) return null
  const points: Point[] = []
  for (const raw of value.points) {
    if (!isObject(raw) || typeof raw.label !== 'string') return null
    const worked = workedOf(raw)
    if (!worked) return null
    points.push({ label: raw.label, ...worked })
  }
  return { ...base, error: null, points }
}

export function boardSummaryOf(value: unknown): BoardSummary | null {
  if (!isObject(value) || typeof value.id !== 'string' || typeof value.name !== 'string') {
    return null
  }
  return {
    id: value.id,
    name: value.name,
    purpose: typeof value.purpose === 'string' ? value.purpose : '',
    origin: value.origin === 'mitra' ? 'mitra' : 'you',
    widgets: integer(value.widgets),
    createdAt: typeof value.created_at === 'string' ? value.created_at : null,
    measures: Array.isArray(value.measures)
      ? value.measures.filter((m): m is string => typeof m === 'string')
      : [],
    template: typeof value.template === 'string' && value.template ? value.template : null,
  }
}

const listOf = <T>(value: unknown, item: (v: unknown) => T | null): T[] =>
  Array.isArray(value) ? value.map(item).filter((v): v is T => v !== null) : []

function versionOf(value: unknown): Version | null {
  if (!isObject(value) || !Number.isSafeInteger(value.index) || typeof value.at !== 'string') {
    return null
  }
  return {
    index: integer(value.index),
    at: value.at,
    by: value.by === 'mitra' ? 'mitra' : 'you',
    label: typeof value.label === 'string' ? value.label : '',
    name: typeof value.name === 'string' ? value.name : '',
    widgets: integer(value.widgets),
  }
}

const SCOPE_KINDS: readonly string[] = ['book', 'symbols', 'watchlist', 'market', 'brokers']

function scopeRowsOf(value: unknown): ScopeRows | null {
  if (!isObject(value) || typeof value.key !== 'string' || typeof value.label !== 'string') {
    return null
  }
  if (!SCOPE_KINDS.includes(value.kind as string) || !strings(value.rows)) return null
  return {
    key: value.key,
    kind: value.kind as Scope['kind'],
    label: value.label,
    rows: value.rows,
  }
}

/** The dashboard body the engine answered with, or null where it is not one. */
export function dashboardBodyOf(value: unknown): DashboardBody | null {
  if (!isObject(value)) return null
  if (value.etag !== null && typeof value.etag !== 'string') return null
  if (!Array.isArray(value.widgets) || !strings(value.holdings) || !strings(value.unavailable)) {
    return null
  }
  const widgets = value.widgets.map(drawnOf)
  if (widgets.some((w) => w === null)) return null
  const head = boardSummaryOf(value.dashboard)
  const sections =
    isObject(value.dashboard) && Array.isArray(value.dashboard.sections)
      ? value.dashboard.sections.map((s) => (typeof s === 'string' ? s : null))
      : []
  return {
    etag: value.etag,
    version: integer(value.version),
    widgets: widgets as Drawn[],
    holdings: value.holdings,
    as_of: typeof value.as_of === 'string' ? value.as_of : null,
    data_state: typeof value.data_state === 'string' ? value.data_state : null,
    unavailable: value.unavailable,
    dashboard: head ? { ...head, sections } : null,
    boards: listOf(value.boards, boardSummaryOf),
    history: listOf(value.history, versionOf),
    scopes: listOf(value.scopes, scopeRowsOf),
    numbers: listOf(value.numbers, myNumberOf),
    existing: value.existing === true,
  }
}

/** A preview's widgets, each echoing the id the client sent with it. */
export function previewBodyOf(value: unknown): PreviewBody | null {
  if (!isObject(value) || !Array.isArray(value.widgets)) return null
  const widgets: Drawn[] = []
  for (const raw of value.widgets) {
    const drawn = drawnOf(raw)
    if (!drawn) return null
    widgets.push(drawn)
  }
  return {
    widgets,
    holdings: strings(value.holdings) ? value.holdings : [],
    scopes: listOf(value.scopes, scopeRowsOf),
  }
}

function vocabularyOf(value: unknown): Vocabulary | null {
  if (!isObject(value)) return null
  if (
    !Array.isArray(value.variables) ||
    !Array.isArray(value.functions) ||
    !Array.isArray(value.presets)
  ) {
    return null
  }
  const variables = value.variables.filter(
    (v): v is Variable =>
      isObject(v) &&
      typeof v.name === 'string' &&
      (v.kind === 'number' || v.kind === 'text') &&
      typeof v.unit === 'string' &&
      typeof v.group === 'string' &&
      typeof v.meaning === 'string',
  )
  const functions = value.functions.filter(
    (f): f is { name: string; meaning: string } =>
      isObject(f) && typeof f.name === 'string' && typeof f.meaning === 'string',
  )
  const presets: Preset[] = []
  for (const p of value.presets) {
    if (
      isObject(p) &&
      typeof p.key === 'string' &&
      typeof p.group === 'string' &&
      typeof p.title === 'string' &&
      typeof p.formula === 'string' &&
      typeof p.about === 'string' &&
      isKind(p.kind) &&
      isUnit(p.unit) &&
      Number.isSafeInteger(p.decimals)
    ) {
      presets.push({
        key: p.key,
        group: p.group,
        title: p.title,
        formula: p.formula,
        about: p.about,
        kind: p.kind,
        unit: p.unit,
        decimals: integer(p.decimals, 2),
        needsSymbol: p.needs_symbol === true,
        // Only what the preset carries: a preset of today's figures has none.
        ...(p.scope !== undefined ? { scope: scopeOf(p.scope) } : {}),
        ...(timeOf(p.time) ? { time: timeOf(p.time) } : {}),
        ...(oneOf(RANKS, p.rank) ? { rank: oneOf(RANKS, p.rank) } : {}),
        ...(typeof p.limit === 'number' && Number.isSafeInteger(p.limit) ? { limit: p.limit } : {}),
      })
    }
  }
  const samples: Vocabulary['samples'] = {}
  if (isObject(value.samples)) {
    for (const [name, sample] of Object.entries(value.samples)) {
      if (
        isObject(sample) &&
        typeof sample.symbol === 'string' &&
        typeof sample.value === 'string'
      ) {
        samples[name] = { symbol: sample.symbol, value: sample.value }
      }
    }
  }
  const held = isObject(value.held) ? value.held : {}
  const aliases: Record<string, string> = {}
  if (isObject(value.aliases)) {
    for (const [word, name] of Object.entries(value.aliases)) {
      if (typeof name === 'string') aliases[word] = name
    }
  }
  const OVERS: readonly string[] = ['book', 'each', 'sector']
  const measures = listOf<Measure>(value.measures, (m) =>
    isObject(m) &&
    typeof m.name === 'string' &&
    typeof m.label === 'string' &&
    typeof m.unit === 'string' &&
    typeof m.group === 'string' &&
    strings(m.over) &&
    m.over.every((o) => OVERS.includes(o))
      ? {
          name: m.name,
          label: m.label,
          unit: m.unit,
          group: m.group,
          over: m.over as Measure['over'],
        }
      : null,
  )
  return {
    variables,
    functions,
    presets,
    samples,
    held: {
      sector: strings(held.sector) ? held.sector : [],
      symbol: strings(held.symbol) ? held.symbol : [],
    },
    aliases,
    measures,
    templates: listOf(value.templates, templateOf),
    watchlists: listOf(value.watchlists, (w) =>
      isObject(w) && Number.isSafeInteger(w.id) && typeof w.name === 'string'
        ? { id: integer(w.id), name: w.name }
        : null,
    ),
    numbers: listOf(value.numbers, myNumberOf),
    phrases: strings(value.phrases) ? value.phrases : [],
    time: timeVocabularyOf(value.time),
    historyVariables: listOf(value.history_variables, (h) =>
      isObject(h) &&
      typeof h.name === 'string' &&
      typeof h.unit === 'string' &&
      typeof h.meaning === 'string'
        ? {
            name: h.name,
            unit: h.unit,
            nature: oneOf(['level', 'flow'] as const, h.nature),
            source: typeof h.source === 'string' ? h.source : null,
            meaning: h.meaning,
          }
        : null,
    ),
    brokerVariables: listOf(value.broker_variables, (b) =>
      isObject(b) &&
      typeof b.name === 'string' &&
      typeof b.unit === 'string' &&
      typeof b.meaning === 'string'
        ? { name: b.name, unit: b.unit, meaning: b.meaning }
        : null,
    ),
    scopes: strings(value.scopes)
      ? (value.scopes.filter((k) => SCOPE_KINDS.includes(k)) as Scope['kind'][])
      : ['book', 'symbols', 'watchlist', 'market'],
    rank: strings(value.rank) ? value.rank.filter((r): r is Rank => oneOf(RANKS, r) !== null) : [],
  }
}

const positive = (value: unknown): number | null =>
  typeof value === 'number' && Number.isSafeInteger(value) && value > 0 ? value : null

/** The time dimension's choices, or null from an engine that offers none. */
function timeVocabularyOf(value: unknown): TimeVocabulary | null {
  if (!isObject(value)) return null
  const windows = listOf(value.windows, (w) => {
    if (!isObject(w) || typeof w.label !== 'string') return null
    const key = oneOf(WINDOWS, w.key)
    return key ? { key, label: w.label, days: positive(w.days) } : null
  })
  if (windows.length === 0) return null
  const every = listOf(value.every, (e) => {
    if (!isObject(e) || typeof e.label !== 'string') return null
    const key = oneOf(EVERIES, e.key)
    return key ? { key, label: e.label } : null
  })
  const defaultEvery: TimeVocabulary['defaultEvery'] = {}
  if (isObject(value.default_every)) {
    for (const [window, step] of Object.entries(value.default_every)) {
      const w = oneOf(WINDOWS, window)
      const e = oneOf(EVERIES, step)
      if (w && e) defaultEvery[w] = e
    }
  }
  const limits = isObject(value.limits) ? value.limits : {}
  return {
    windows,
    every,
    show: strings(value.show)
      ? value.show.filter((s): s is TimeShow => oneOf(SHOWS, s) !== null)
      : [],
    defaultEvery,
    limits: {
      symbols: positive(limits.symbols),
      floorsheetSymbols: positive(limits.floorsheet_symbols),
      floorsheetSessions: positive(limits.floorsheet_sessions),
      steps: positive(limits.steps),
      customDays: positive(limits.custom_days),
    },
  }
}

function templateOf(value: unknown): Template | null {
  if (
    !isObject(value) ||
    typeof value.key !== 'string' ||
    typeof value.name !== 'string' ||
    typeof value.purpose !== 'string' ||
    !Array.isArray(value.sections)
  ) {
    return null
  }
  return {
    key: value.key,
    name: value.name,
    purpose: value.purpose,
    sections: listOf(value.sections, (s) =>
      isObject(s) && typeof s.title === 'string' && strings(s.measures)
        ? { title: s.title, measures: s.measures }
        : null,
    ),
    needsSymbol: value.needs_symbol === true,
  }
}

async function bodyOf(response: Response): Promise<unknown> {
  try {
    return (await response.json()) as unknown
  } catch {
    return null
  }
}

export type DashboardRead =
  | { kind: 'ok'; body: DashboardBody }
  /** The engine has no dashboards configured (503 "not configured"), or no route. */
  | { kind: 'unavailable' }
  /** No dashboard by that id among the reader's own. */
  | { kind: 'missing' }
  | { kind: 'failed' }

/** `GET /v1/dashboard`: the first dashboard with its values, worked out now. */
export async function readDashboard(): Promise<DashboardRead> {
  return readBoard(null)
}

/**
 * One dashboard by id (`GET /v1/dashboards/{id}`), or the first one with
 * `null`. A dashboard the reader no longer has is `missing`, so the screen can
 * say so rather than drawing an engine outage.
 */
export async function readBoard(id: string | null): Promise<DashboardRead> {
  if (id !== null && !BOARD_ID.test(id)) return { kind: 'missing' }
  try {
    const response = await engineFetch(boardPath(id), { timeoutMs: 30_000 })
    if (id !== null && response.status === 404) return { kind: 'missing' }
    if (response.status === 404 || response.status === 503) return { kind: 'unavailable' }
    if (!response.ok) return { kind: 'failed' }
    const body = dashboardBodyOf(await bodyOf(response))
    return body ? { kind: 'ok', body } : { kind: 'failed' }
  } catch {
    return { kind: 'failed' }
  }
}

export type SaveResult =
  | { kind: 'saved'; body: DashboardBody }
  /** Another tab saved first: the reader reloads, nothing is written over it. */
  | { kind: 'conflict' }
  /**
   * The engine refused, with its reason and the widget it concerns. `code`
   * where the screen acts on it: `name_taken` names the dashboard that has
   * the name (`existing`), so the reader can open it.
   */
  | {
      kind: 'refused'
      message: string
      widget: string | null
      code?: string | null | undefined
      existing?: string | null | undefined
    }
  | { kind: 'failed' }

/** The widget as the engine saves it: the definition and no more. */
const definition = (w: WidgetDraft) => ({
  ...(w.id ? { id: w.id } : {}),
  title: w.title,
  formula: w.formula,
  kind: w.kind,
  unit: w.unit,
  decimals: w.decimals,
  // Sent back whenever the draft carries them, a cleared heading as null and
  // the book as itself: the engine keeps a stored section or scope a save
  // leaves out, so leaving one out could never clear it.
  ...(w.section !== undefined ? { section: w.section } : {}),
  ...(w.scope !== undefined ? { scope: w.scope } : {}),
  // The same for the time dimension: the engine keeps a stored window a save
  // leaves out, so `null` is how a series is turned back into today's figure.
  ...(w.time !== undefined ? { time: w.time } : {}),
  ...(w.rank !== undefined ? { rank: w.rank } : {}),
  ...(w.limit !== undefined ? { limit: w.limit } : {}),
  ...(w.where !== undefined ? { where: w.where } : {}),
})

export function myNumberOf(value: unknown): MyNumber | null {
  if (!isObject(value) || typeof value.name !== 'string' || typeof value.label !== 'string') {
    return null
  }
  const values: Record<string, string> = {}
  if (isObject(value.values)) {
    for (const [symbol, v] of Object.entries(value.values)) {
      if (typeof v === 'string' && DECIMAL.test(v)) values[symbol] = v
    }
  }
  return {
    name: value.name,
    label: value.label,
    unit: NUMBER_UNITS.includes(value.unit as NumberUnit) ? (value.unit as NumberUnit) : 'ratio',
    value: typeof value.value === 'string' && DECIMAL.test(value.value) ? value.value : null,
    values,
  }
}

/** A name as the engine compares two: spacing and case ignored. */
export const nameKey = (name: string): string => name.trim().replace(/\s+/g, ' ').toLowerCase()

/**
 * The reader's dashboard already called `name`, other than `but`, or null.
 * NAMES ARE UNIQUE (owner, 2026-10-20): checked here before asking, so the
 * reader hears it as they type; the engine still refuses one (`name_taken`).
 */
export function nameTaken(
  boards: readonly BoardSummary[],
  name: string,
  but?: string,
): BoardSummary | null {
  const wanted = nameKey(name)
  if (!wanted) return null
  return boards.find((b) => b.id !== but && nameKey(b.name) === wanted) ?? null
}

/** A dashboard id as the engine mints one; nothing else goes in a path. */
export const BOARD_ID = /^d_[0-9a-f]{10}$/

const boardPath = (id: string | null) =>
  id === null ? '/v1/dashboard' : `/v1/dashboards/${encodeURIComponent(id)}`

/** A save's answer: the body it drew, or why nothing was written. */
async function savedOf(response: Response): Promise<SaveResult> {
  const body = await bodyOf(response)
  if (response.ok) {
    const saved = dashboardBodyOf(body)
    return saved ? { kind: 'saved', body: saved } : { kind: 'failed' }
  }
  const detail = isObject(body) ? body.detail : undefined
  // A TAKEN NAME IS A 409 TOO (contract §6), but not a stale version: it
  // carries `code: "name_taken"` and the dashboard that has the name, and
  // reloading would change nothing. Only a 409 without it is a conflict.
  const named = isObject(detail) && detail.code === 'name_taken'
  if (response.status === 409 && !named) return { kind: 'conflict' }
  if (response.status === 422 || named) {
    if (typeof detail === 'string') return { kind: 'refused', message: detail, widget: null }
    if (isObject(detail) && typeof detail.message === 'string') {
      // The dashboard holding a taken name: `existing_id` (contract §6), or
      // `existing` as an earlier engine named it; only an id this app can open.
      const existing = [detail.existing_id, detail.existing, detail.dashboard_id].find(
        (v): v is string => typeof v === 'string' && BOARD_ID.test(v),
      )
      return {
        kind: 'refused',
        message: detail.message,
        widget: typeof detail.widget === 'string' ? detail.widget : null,
        code: typeof detail.code === 'string' ? detail.code : null,
        existing: existing ?? null,
      }
    }
  }
  return { kind: 'failed' }
}

/** `PUT /v1/dashboard`: the first dashboard's whole list, conditional on the version shown. */
export async function saveDashboard(
  etag: string | null,
  widgets: readonly WidgetDraft[],
): Promise<SaveResult> {
  return saveBoard(null, etag, widgets)
}

/**
 * Save one dashboard's whole list (and, where given, its name and purpose),
 * conditional on the version shown: `PUT /v1/dashboards/{id}`, or the first
 * dashboard with `null`.
 */
export async function saveBoard(
  id: string | null,
  etag: string | null,
  widgets: readonly WidgetDraft[],
  head: { name?: string | undefined; purpose?: string | undefined } = {},
): Promise<SaveResult> {
  if (id !== null && !BOARD_ID.test(id)) return { kind: 'failed' }
  try {
    const response = await engineFetch(boardPath(id), {
      method: 'PUT',
      json: {
        etag,
        widgets: widgets.map(definition),
        ...(head.name ? { name: head.name } : {}),
        ...(head.purpose !== undefined ? { purpose: head.purpose } : {}),
      },
      timeoutMs: 30_000,
    })
    return await savedOf(response)
  } catch {
    return { kind: 'failed' }
  }
}

/** `POST /v1/dashboards`: a new dashboard, empty or a whole one from a template. */
export async function createBoard(input: {
  etag: string | null
  name?: string | undefined
  purpose?: string | undefined
  template?: string | undefined
  /** The company a one-company template (`needsSymbol`) is about. */
  symbol?: string | undefined
}): Promise<SaveResult> {
  try {
    const response = await engineFetch('/v1/dashboards', {
      method: 'POST',
      json: {
        etag: input.etag,
        ...(input.name ? { name: input.name } : {}),
        ...(input.purpose ? { purpose: input.purpose } : {}),
        ...(input.template ? { template: input.template } : {}),
        ...(input.template && input.symbol ? { symbol: input.symbol } : {}),
      },
      timeoutMs: 30_000,
    })
    return await savedOf(response)
  } catch {
    return { kind: 'failed' }
  }
}

/** `POST /v1/dashboards/{id}/restore`: the dashboard as one kept version left it. */
export async function restoreBoard(
  id: string,
  etag: string | null,
  index: number,
): Promise<SaveResult> {
  if (!BOARD_ID.test(id)) return { kind: 'failed' }
  try {
    const response = await engineFetch(`/v1/dashboards/${id}/restore`, {
      method: 'POST',
      json: { etag, index },
      timeoutMs: 30_000,
    })
    return await savedOf(response)
  } catch {
    return { kind: 'failed' }
  }
}

/**
 * `POST /v1/dashboards/{id}/widgets/{widgetId}/move`: one widget moved, as
 * it is, to the top of another of the reader's dashboards, conditional on the
 * version shown. The answer is the destination dashboard, as a read draws it.
 */
export async function moveWidget(
  id: string,
  widgetId: string,
  etag: string | null,
  toId: string,
  to = 0,
): Promise<SaveResult> {
  if (!BOARD_ID.test(id) || !BOARD_ID.test(toId) || !/^w_[0-9a-z]+$/.test(widgetId)) {
    return { kind: 'failed' }
  }
  try {
    const response = await engineFetch(
      `/v1/dashboards/${id}/widgets/${encodeURIComponent(widgetId)}/move`,
      { method: 'POST', json: { etag, to_dashboard_id: toId, to }, timeoutMs: 30_000 },
    )
    return await savedOf(response)
  } catch {
    return { kind: 'failed' }
  }
}

export type BoardsRead =
  | { kind: 'ok'; etag: string | null; boards: BoardSummary[]; templates: Template[] }
  | { kind: 'unavailable' }
  | { kind: 'failed' }

/** `GET /v1/dashboards`: the reader's dashboards by name, and the templates. */
export async function listBoards(): Promise<BoardsRead> {
  try {
    const response = await engineFetch('/v1/dashboards', { timeoutMs: 20_000 })
    if (response.status === 404 || response.status === 503) return { kind: 'unavailable' }
    if (!response.ok) return { kind: 'failed' }
    const body = await bodyOf(response)
    if (!isObject(body) || (body.etag !== null && typeof body.etag !== 'string')) {
      return { kind: 'failed' }
    }
    return {
      kind: 'ok',
      etag: body.etag,
      boards: listOf(body.boards, boardSummaryOf),
      templates: listOf(body.templates, templateOf),
    }
  } catch {
    return { kind: 'failed' }
  }
}

export type RemoveResult =
  | { kind: 'removed'; etag: string | null; boards: BoardSummary[] }
  | { kind: 'conflict' }
  | { kind: 'failed' }

/** `DELETE /v1/dashboards/{id}`, conditional on the version shown. */
export async function removeBoard(id: string, etag: string | null): Promise<RemoveResult> {
  if (!BOARD_ID.test(id)) return { kind: 'failed' }
  try {
    const response = await engineFetch(`/v1/dashboards/${id}`, {
      method: 'DELETE',
      json: { etag },
      timeoutMs: 20_000,
    })
    if (response.status === 409) return { kind: 'conflict' }
    if (!response.ok) return { kind: 'failed' }
    const body = await bodyOf(response)
    if (!isObject(body)) return { kind: 'failed' }
    return {
      kind: 'removed',
      etag: typeof body.etag === 'string' ? body.etag : null,
      boards: listOf(body.boards, boardSummaryOf),
    }
  } catch {
    return { kind: 'failed' }
  }
}

export interface ComposeChoice {
  measure: string
  over: 'book' | 'each' | 'sector'
  sector?: string | undefined
  symbol?: string | undefined
  scope?: Scope | undefined
  /** The same measure over a window, with its order where it has rows. */
  time?: TimeSpec | undefined
  rank?: Rank | undefined
  limit?: number | undefined
}

export type Composed =
  | { kind: 'ok'; widget: WidgetDraft; preview: Drawn | null }
  | { kind: 'refused'; message: string }
  | { kind: 'failed' }

/**
 * `POST /v1/dashboard/compose`: a widget from choices -- measure, for what,
 * only which -- with the formula a careful analyst would write and its value
 * now. The reader never has to write the formula; it is still shown.
 */
export async function composeWidget(choice: ComposeChoice): Promise<Composed> {
  try {
    const response = await engineFetch('/v1/dashboard/compose', {
      method: 'POST',
      json: {
        measure: choice.measure,
        over: choice.over,
        ...(choice.sector ? { sector: choice.sector } : {}),
        ...(choice.symbol ? { symbol: choice.symbol } : {}),
        ...(choice.scope && choice.scope.kind !== 'book' ? { scope: choice.scope } : {}),
        ...(choice.time ? { time: choice.time } : {}),
        ...(choice.rank ? { rank: choice.rank } : {}),
        ...(choice.limit ? { limit: choice.limit } : {}),
      },
      timeoutMs: 30_000,
    })
    const body = await bodyOf(response)
    if (response.status === 422 && isObject(body) && isObject(body.detail)) {
      const message = body.detail.message
      return { kind: 'refused', message: typeof message === 'string' ? message : 'Not available' }
    }
    if (!response.ok || !isObject(body) || !isObject(body.widget)) return { kind: 'failed' }
    const w = body.widget
    if (
      typeof w.title !== 'string' ||
      typeof w.formula !== 'string' ||
      !isKind(w.kind) ||
      !isUnit(w.unit)
    ) {
      return { kind: 'failed' }
    }
    return {
      kind: 'ok',
      widget: {
        title: w.title,
        formula: w.formula,
        kind: w.kind,
        unit: w.unit,
        decimals: integer(w.decimals, 2),
        scope: scopeOf(w.scope),
        // Echoed only where set, so a composed figure of today stays one.
        ...(timeOf(w.time) ? { time: timeOf(w.time) } : {}),
        ...(oneOf(RANKS, w.rank) ? { rank: oneOf(RANKS, w.rank) } : {}),
        ...(typeof w.limit === 'number' && Number.isSafeInteger(w.limit) ? { limit: w.limit } : {}),
      },
      preview: drawnOf(body.preview),
    }
  } catch {
    return { kind: 'failed' }
  }
}

/**
 * `POST /v1/dashboard/preview`: widgets worked out without saving them, each
 * answering alone, so one that cannot be read says why beside the others'
 * values. Null on any failure; a preview is never worth an error page.
 */
export async function previewWidgets(widgets: readonly WidgetDraft[]): Promise<PreviewBody | null> {
  if (widgets.length === 0) return { widgets: [], holdings: [], scopes: [] }
  try {
    const response = await engineFetch('/v1/dashboard/preview', {
      method: 'POST',
      json: { widgets: widgets.map(definition) },
      timeoutMs: 20_000,
    })
    if (!response.ok) return null
    return previewBodyOf(await bodyOf(response))
  } catch {
    return null
  }
}

/** `DELETE /v1/dashboard`: true where the engine answered 2xx. */
export async function clearDashboard(): Promise<boolean> {
  try {
    return (await engineFetch('/v1/dashboard', { method: 'DELETE' })).ok
  } catch {
    return false
  }
}

/** `GET /v1/dashboard/vocabulary`: the names, functions and presets, or null. */
export async function readVocabulary(): Promise<Vocabulary | null> {
  try {
    const response = await engineFetch('/v1/dashboard/vocabulary', { timeoutMs: 20_000 })
    if (!response.ok) return null
    return vocabularyOf(await bodyOf(response))
  } catch {
    return null
  }
}
